import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { bnplServices, comparisonCategories, faqs } from '../data/mock';

const Compare = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/">
              <Button variant="ghost">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">Service Comparison</h1>
            <div className="w-24"></div>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Detailed Comparison</h2>
            <p className="text-slate-600 text-lg">
              Compare rates, fees, and features side-by-side
            </p>
          </div>

          {/* Desktop Comparison Table */}
          <div className="hidden lg:block overflow-x-auto">
            <div className="bg-white rounded-xl shadow-lg border-2 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                    <th className="p-6 text-left font-bold text-lg">Feature</th>
                    {bnplServices.map((service) => (
                      <th key={service.id} className="p-6 text-center font-bold text-lg">
                        <div className="flex flex-col items-center gap-2">
                          <span>{service.name}</span>
                          <Badge variant="secondary" className="bg-white/20 text-white">
                            {service.type}
                          </Badge>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonCategories.map((category, catIdx) => (
                    <React.Fragment key={catIdx}>
                      <tr className="bg-slate-100">
                        <td colSpan={4} className="p-4 font-bold text-lg text-slate-700">
                          {category.category}
                        </td>
                      </tr>
                      {category.items.map((item, itemIdx) => (
                        <tr key={itemIdx} className="border-b hover:bg-blue-50/50 transition-colors">
                          <td className="p-4 font-medium text-slate-700">{item.feature}</td>
                          <td className="p-4 text-center text-slate-600">{item.beforepay}</td>
                          <td className="p-4 text-center text-slate-600">{item.afterpay}</td>
                          <td className="p-4 text-center text-slate-600">{item.klarna}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Comparison Cards */}
          <div className="lg:hidden space-y-6">
            {bnplServices.map((service) => (
              <Card key={service.id} className="border-2">
                <CardHeader className="bg-gradient-to-br from-blue-50 to-purple-50">
                  <CardTitle className="text-2xl">{service.name}</CardTitle>
                  <CardDescription>
                    <Badge>{service.type}</Badge>
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  {comparisonCategories.map((category, catIdx) => (
                    <div key={catIdx} className="mb-6 last:mb-0">
                      <h4 className="font-bold text-lg mb-3 text-slate-700">{category.category}</h4>
                      <div className="space-y-2">
                        {category.items.map((item, itemIdx) => (
                          <div key={itemIdx} className="flex justify-between py-2 border-b last:border-b-0">
                            <span className="text-sm font-medium text-slate-600">{item.feature}</span>
                            <span className="text-sm text-slate-700 font-semibold">
                              {service.id === 'beforepay' ? item.beforepay : 
                               service.id === 'afterpay' ? item.afterpay : 
                               item.klarna}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pros & Cons */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Pros & Cons</h2>
            <p className="text-slate-600 text-lg">
              Understand the advantages and limitations of each service
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {bnplServices.map((service) => (
              <Card key={service.id} className="border-2">
                <CardHeader>
                  <CardTitle className="text-2xl">{service.name}</CardTitle>
                  <CardDescription>{service.tagline}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <h4 className="font-bold text-lg mb-3 text-green-700 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Pros
                    </h4>
                    <ul className="space-y-2">
                      {service.pros.map((pro, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{pro}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-3 text-red-700 flex items-center gap-2">
                      <XCircle className="h-5 w-5" />
                      Cons
                    </h4>
                    <ul className="space-y-2">
                      {service.cons.map((con, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{con}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link to={`/services/${service.id}`} className="block mt-6">
                    <Button className="w-full" variant="outline">
                      View Full Details
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-slate-600 text-lg">
              Get answers to common questions about BNPL services
            </p>
          </div>
          <Card className="border-2">
            <CardContent className="pt-6">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`}>
                    <AccordionTrigger className="text-left font-semibold">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Choose Your Service?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
            Visit the official websites to sign up and start shopping
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {bnplServices.map((service) => (
              <a key={service.id} href={service.website} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-slate-100">
                  Visit {service.name}
                </Button>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Compare;